/**
 * Demo Booking API Service (S3)
 *
 * The member demo write surface — create + cancel against
 * `/public/v1/demo-bookings`. Envelope-returning like `bookingService`
 * (the hooks are the throwing layer, per the internalApi convention).
 * The create response reuses `ApiCreateBookingResponse`, deliberately the
 * same shape as the standard create, so the confirmation page consumes
 * either through one code path.
 *
 * @module booking/api/demoBookingService
 */

import { apiClient } from '~/lib/apiClient';
import { appRuntime } from '~/services/appRuntime/appRuntimeService';
import type { ApiResult } from '~/lib/apiClient/types';
import type { ApiCreateBookingResponse } from './types';

export interface CreateDemoBookingPayload {
  clubId: number;
  bayId: number;
  userId: number;
  startTime: string; // ISO 8601 UTC
  endTime: string; // ISO 8601 UTC
}

export const demoBookingService = {
  /**
   * Book a demo for the authenticated member. No payment fields — a demo is
   * free by definition; the server enforces the demo rules (window, notice,
   * session length) and refuses rather than warns.
   */
  async createDemoBooking(
    payload: CreateDemoBookingPayload
  ): Promise<ApiResult<ApiCreateBookingResponse>> {
    const tenantHandle = appRuntime.tenantHandle;
    const clubId = appRuntime.clubId;

    return apiClient.post<ApiCreateBookingResponse>(
      '/demo-bookings',
      payload,
      {
        tenantHandle: tenantHandle || undefined,
        clubId: clubId ? parseInt(clubId) : undefined,
      }
    );
  },

  /**
   * Cancel the member's own demo. Ownership is server-side: someone else's
   * demo answers 404. A demo that already took place refuses 409.
   */
  async cancelDemoBooking(
    bookingId: number
  ): Promise<ApiResult<{ bookingId: number }>> {
    const tenantHandle = appRuntime.tenantHandle;
    const clubId = appRuntime.clubId;

    return apiClient.post<{ bookingId: number }>(
      `/demo-bookings/${bookingId}/cancel`,
      // clubId in the body so the club-access middleware can resolve the club.
      { clubId: clubId ? parseInt(clubId) : undefined },
      {
        tenantHandle: tenantHandle || undefined,
        clubId: clubId ? parseInt(clubId) : undefined,
      }
    );
  },
};
